'use client';

import { useCallback } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { Search, X } from 'lucide-react';
import { cn } from '@/lib/utils';
import Input from '@/components/ui/Input';
import Button from '@/components/ui/Button';
import type { BlogCategory, BlogTag } from '@/lib/api/content';

interface BlogFilterBarProps {
  categories: BlogCategory[];
  tags: BlogTag[];
  activeCategory?: string;
  activeTag?: string;
  searchQuery?: string;
}

export function BlogFilterBar({
  categories,
  tags,
  activeCategory,
  activeTag,
  searchQuery = '',
}: BlogFilterBarProps) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const setQuery = useCallback(
    (updates: Record<string, string | undefined>) => {
      const params = new URLSearchParams(searchParams.toString());
      Object.entries(updates).forEach(([key, value]) => {
        if (value === undefined || value === '') {
          params.delete(key);
        } else {
          params.set(key, value);
        }
      });
      // Reset to first page when filters change
      params.delete('page');
      router.push(`${pathname}?${params.toString()}`);
    },
    [router, pathname, searchParams]
  );

  const handleSearchSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.currentTarget);
    const search = formData.get('search')?.toString() ?? '';
    setQuery({ search });
  };

  const activeCategoryName = activeCategory
    ? categories.find((c) => c.slug === activeCategory)?.name
    : undefined;
  const activeTagName = activeTag ? tags.find((t) => t.slug === activeTag)?.name : undefined;

  return (
    <div className="space-y-4 rounded-xl border border-neon-cyan/10 bg-panel/60 p-4 backdrop-blur-md">
      {/* Search */}
      <form onSubmit={handleSearchSubmit} className="flex gap-2">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
          <Input
            name="search"
            type="search"
            placeholder="Search articles..."
            defaultValue={searchQuery}
            className="pl-9"
            aria-label="Search articles"
          />
        </div>
        <Button type="submit">Search</Button>
      </form>

      {/* Active filters */}
      {(activeCategoryName || activeTagName || searchQuery) && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-ash">Active filters:</span>
          {activeCategoryName && (
            <button
              type="button"
              onClick={() => setQuery({ category: undefined })}
              className="inline-flex min-h-11 items-center gap-1 rounded-full bg-neon-cyan/10 px-2.5 py-1.5 text-xs font-medium text-neon-cyan hover:bg-neon-cyan/15"
            >
              {activeCategoryName}
              <X className="h-3 w-3" />
            </button>
          )}
          {activeTagName && (
            <button
              type="button"
              onClick={() => setQuery({ tag: undefined })}
              className="inline-flex min-h-11 items-center gap-1 rounded-full bg-neon-purple/10 px-2.5 py-1.5 text-xs font-medium text-neon-purple hover:bg-neon-purple/15"
            >
              {activeTagName}
              <X className="h-3 w-3" />
            </button>
          )}
          {searchQuery && (
            <button
              type="button"
              onClick={() => setQuery({ search: undefined })}
              className="inline-flex min-h-11 items-center gap-1 rounded-full bg-neon-amber/10 px-2.5 py-1.5 text-xs font-medium text-neon-amber hover:bg-neon-amber/15"
            >
              &ldquo;{searchQuery}&rdquo;
              <X className="h-3 w-3" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setQuery({ category: undefined, tag: undefined, search: undefined })}
            className="inline-flex min-h-11 items-center text-xs text-ash underline-offset-2 hover:text-neon-cyan hover:underline"
          >
            Clear all
          </button>
        </div>
      )}

      {/* Categories */}
      {categories.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-ash">Categories</span>
          <div className="flex flex-wrap gap-2">
            {categories.map((category) => (
              <button
                key={category.id}
                type="button"
                onClick={() => setQuery({ category: category.slug })}
                className={cn(
                  'inline-flex min-h-11 items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  activeCategory === category.slug
                    ? 'border-neon-cyan/30 bg-neon-cyan/10 text-neon-cyan'
                    : 'border-border bg-panel/80 text-ash hover:border-neon-cyan/20 hover:text-foreground'
                )}
                aria-pressed={activeCategory === category.slug}
              >
                {category.name}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Tags */}
      {tags.length > 0 && (
        <div className="space-y-2">
          <span className="text-xs font-medium uppercase tracking-wider text-ash">Tags</span>
          <div className="flex flex-wrap gap-2">
            {tags.map((tag) => (
              <button
                key={tag.id}
                type="button"
                onClick={() => setQuery({ tag: tag.slug })}
                className={cn(
                  'inline-flex min-h-11 items-center rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                  activeTag === tag.slug
                    ? 'border-neon-purple/30 bg-neon-purple/10 text-neon-purple'
                    : 'border-border bg-panel/80 text-ash hover:border-neon-purple/20 hover:text-foreground'
                )}
                aria-pressed={activeTag === tag.slug}
              >
                {tag.name}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default BlogFilterBar;
